import { LogIn } from "lucide-react";
import { type ReactNode } from "react";
import anthmaiLogo from "@/assets/anthmai-icon.svg";
import { useAuth } from "@/context/AuthContext";
import { PlatformBlocked } from "./PlatformBlocked";

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: Readonly<ProtectedRouteProps>) {
  const { isAuthenticated, isLoading, login, providerName, platformBlocked } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-base">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-primary border-t-transparent mx-auto" />
          <p className="mt-4 text-content-tertiary">Loading...</p>
        </div>
      </div>
    );
  }

  // Show blocked page if the platform is suspended/expired/seat-limited
  if (platformBlocked.reason) {
    return <PlatformBlocked reason={platformBlocked.reason} message={platformBlocked.message ?? undefined} />;
  }

  if (!isAuthenticated) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-base">
        <div className="text-center">
          <img src={anthmaiLogo} alt="AnthmAI" className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-content-primary">AnthmAI</h1>
          <p className="mt-2 text-content-tertiary">Enterprise AI Platform</p>
          <button
            onClick={() => login()}
            className="mt-6 flex items-center gap-2 px-6 py-3 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity mx-auto"
          >
            <LogIn className="h-4 w-4" />
            Sign in with {providerName}
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
